<template>
  <div>
    <b-modal v-model="active" size="lg" centered hide-footer no-close-on-backdrop body-class="p-0" @hidden="reset">
      <template #modal-title>{{ $t("Service") }}</template>
      <div>
        <b-card-body v-if="service != null">
          <div>
           <!-- Displaying Client data -->
            <b-row class="mb-1" v-if="!isNewService">
              <b-col cols="12" class="text-capitalize"><b>{{ $t("Client") }}:</b> {{ service.clientName }}</b-col>
              <b-col cols="12" class="text-capitalize"><b>{{ $t("Contract") }}:</b> {{ service.contractAddress }}</b-col>
            </b-row>
          </div>
          <b-row>
            <b-col cols="6">
              <b-form-group :label="$t('Provider')">
                <providers-search v-if="isNewService" v-bind:add-allowed="hasProviderPermission" @selected="onProviderSelected"/>
                <b-form-input v-else v-model="service.name" readonly></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group :label="$t('Status')">
                <v-select
                    v-bind:disabled="providerIsNotSet"
                    :options="statusOptions"
                    v-model="service.status"
                    label="text"
                    :reduce="(option) => option.value"
                    :clearable="false"
                ></v-select>
              </b-form-group>
            </b-col>
            <b-col cols="4">
              <b-form-group :label="$t('Price')">
                <b-form-input v-model="service.price" type="number" @input="onPriceUpdated"
                              v-bind:readonly="providerIsNotSet"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="4">
              <b-form-group :label="$t('Amount')">
                <b-form-input v-model="service.amount" type="number" @input="onPriceUpdated"
                              v-bind:readonly="providerIsNotSet"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="4">
              <b-form-group :label="$t('Service Time')">
                <vue-ctk-date-time-picker noClearButton v-bind:disabled="providerIsNotSet"
                                          label=""
                                          :locale="currentFormattedLang"
                                          v-model="service.serviceTime"
                                          format="YYYY-MM-DD HH:mm"
                                          formatted="DD/MM/YY HH:mm"
                >
                </vue-ctk-date-time-picker>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group :label="$t('Commission')">
                <b-form-input v-model="service.commission" type="number"
                              v-bind:readonly="providerIsNotSet || !priceIsSet"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group :label="$t('Commission Percent')">
                <b-form-input v-bind:value="commissionPercent" type="number" disabled></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group :label="$t('Note')">
                <b-form-textarea v-model="service.note" v-bind:readonly="providerIsNotSet"></b-form-textarea>
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group>
                <b-form-checkbox v-model="service.paid" inline>
                  {{ $t('Paid') }}
                </b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-button block variant="primary" @click="saveService" v-bind:disabled="saveIsDisabled">
                {{ $t('Save') }}
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import ProvidersSearch from "@/components/widgets/picker/providersSearch.vue";
import Permissions from "@/model/permissions/permissionNames";


const generateServiceRequest = (service, systemName, systemId, contractId) => {
  service = service || {};
  return {
    providerId: service.providerId || null,
    price: service.price || null,
    commission: service.commission || null,
    amount: service.amount || 1,
    serviceTime: service.serviceTime || null,
    note: service.note || null,
    paid: service.paid || false,
    status: service.status,
    contractId: contractId || null,
    systemRequestName: systemName || null,
    systemRequestId: systemId || null,
  };
}

const generateService = (service, systemName, systemId, contractId) => {
  service = service || {};
  return {
    id: service.id || null,
    providerId: service.providerId || null,
    name: service.name || null,
    clientId: service.clientId || null,
    clientName: service.clientName || null,
    contractId: contractId || service.contractId || null,
    contractAddress: service.contractAddress || null,
    commission: service.commission || null,
    commissionPercent: service.commissionPercent || null,
    price: service.price || null,
    amount: service.amount || 1,
    requestTime: service.requestTime || null,
    serviceTime: service.serviceTime || null,
    note: service.note || null,
    systemRequestName: systemName || null,
    paid: service.paid || false,
    status: service.status || "PENDING",
    systemRequestId: systemId || null,
  };
};

export default {
  name: "ProviderServicesModal",
  components: {ProvidersSearch},
  data: () => ({
    active: false,
    busy: false,
    serviceId: null,
    service: null,
    defaultPrice: null,
    contractId: null,
  }),
  props: [
    "systemName",
    "systemId",
  ],
  computed: {
    ...mapGetters('translator', ['msg', 'currentFormattedLang']),
    ...mapGetters("user", ['canRead']),
    providerIsNotSet() {
      return this.service == null || this.service.providerId == null;
    },
    isNewService() {
      return this.serviceId == null;
    },
    priceIsSet(){
      return this.service != null && this.service.price != null && this.service.price > 0 &&
          this.service.amount != null && this.service.amount > 0;
    },
    commissionPercent() {
      if (this.service == null) return null;
      if (this.service.commission == null || this.service.price == null) return null;
      let commissionPercent = (this.service.commission / (this.service.price * this.service.amount)) * 100;
      return isNaN(commissionPercent) ? null : commissionPercent.toFixed(2);
    },
    saveIsDisabled() {
      if (this.service == null) return true;
      if (this.service.providerId == null) return true;
      if (this.service.price == null || this.service.price <= 0) return true;
      if (this.service.amount == null || this.service.amount <= 0) return true;
      if (this.service.commission == null || this.service.commission < 0) return true;
      if (this.service.serviceTime == null || this.service.serviceTime.trim() === "") return true;
      return this.busy;
    },
    statusOptions() {
      let options = this.$t("providerServiceStatus");
      return Object.keys(options).map((key) => {
        return {value: key, text: options[key]};
      });
    },
    hasProviderPermission() {
      return this.canRead(Permissions.PROVIDER.name);
    }
  },
  methods: {
    ...mapActions("providersServices", ["fetchServiceById", "insertService", "updateService"]),

    show(serviceId, contractId, defaultPrice, checkInDate) {
      console.log("show service modal", {serviceId, contractId, defaultPrice, checkInDate});
      this.contractId = contractId;
      this.defaultPrice = defaultPrice == null || defaultPrice < 0 ? null : defaultPrice;
      if(checkInDate != null && checkInDate !== "" && this.$moment(checkInDate, "YYYY-MM-DD").isValid()) {
        checkInDate = this.$moment(checkInDate, "YYYY-MM-DD").hour(12).minute(0).format("YYYY-MM-DD HH:mm");
      } else {
        checkInDate = null;
      }
      if (serviceId == null) {
        this.service = {...generateService(), serviceTime:checkInDate };
      } else {
        this.serviceId = serviceId;
        this.refresh();
      }
      this.active = true;
    },
    refresh() {
      if (this.serviceId == null) {
        return;
      }
      let $this = this;
      this.fetchServiceById(this.serviceId).then((service) => {
        $this.service = generateService(service);
      });
    },
    reset() {
      this.active = false;
      this.service = null;
      this.serviceId = null;
    },
    onPriceUpdated() {
      if (this.service == null || this.service.price == null || this.defaultPrice == null) {
        return;
      }
      let amount = this.service.amount;
      if (isNaN(amount)) {
        amount = 1;
      }
      if (this.service.price * amount > this.defaultPrice) {
        this.service.commission = 0;
      } else {
        this.service.commission = this.defaultPrice - (this.service.price * amount);
      }
    },
    onProviderSelected(providerId) {
      if (providerId == null) {
        this.service.providerId = null;
        return;
      }
      this.service.providerId = providerId;
    },
    saveService() {
      let body = generateServiceRequest(this.service, this.systemName, this.systemId, this.contractId);
      let $this = this;
      this.busy = true;
      let request = null;
      if (this.isNewService) {
        request = this.insertService(body);
      } else {
        request = this.updateService({id: this.serviceId, request: body});
      }
      request.then((service) => {
            $this.$emit("updated", service);
            $this.serviceId = service.id;
            $this.reset();
          }, () => {
          })
          .finally(() => {
            $this.$emit("refresh");
            $this.busy = false;
          });
    },
  },
};
</script>

<style scoped>
</style>